import React, { useState, useEffect, useRef } from 'react';
import { Employee, Department, Job, EmployeeFormData } from '../types/models';
import { EmployeeService } from '../services/employeeService';
import { DepartmentService } from '../services/departmentService';
import { JobService } from '../services/jobService';

interface EmployeeAutocompleteProps {
  value?: number;
  onChange: (employeeId: number | null, employee?: Employee) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}

export const EmployeeAutocomplete: React.FC<EmployeeAutocompleteProps> = ({
  value,
  onChange,
  placeholder = 'Начните вводить имя сотрудника...',
  required = false,
  disabled = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newEmployeeData, setNewEmployeeData] = useState<EmployeeFormData>({
    surname: '',
    name: '',
    patronymic: '',
    departmentId: 0,
    jobIds: [],
    mainJobId: undefined
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Загружаем данные при инициализации
  useEffect(() => {
    loadReferenceData();
  }, []);

  // Загружаем выбранного сотрудника при инициализации
  useEffect(() => {
    if (value && value > 0) {
      loadSelectedEmployee(value);
    } else {
      setSelectedEmployee(null);
      setSearchTerm('');
    }
  }, [value]);

  const loadReferenceData = async () => {
    try {
      const [departmentsResponse, jobsResponse] = await Promise.all([
        DepartmentService.getAll(),
        JobService.getAll()
      ]);

      if (departmentsResponse.success) {
        setDepartments(departmentsResponse.data);
      }

      if (jobsResponse.success) {
        setJobs(jobsResponse.data);
      }
    } catch (error) {
      console.error('Ошибка загрузки справочных данных:', error);
    }
  };

  const loadSelectedEmployee = async (id: number) => {
    try {
      const response = await EmployeeService.getById(id);
      if (response.success && response.data) {
        setSelectedEmployee(response.data);
        setSearchTerm(`${response.data.surname} ${response.data.name} ${response.data.patronymic}`.trim());
      }
    } catch (error) {
      console.error('Ошибка загрузки сотрудника:', error);
    }
  };

  // Поиск сотрудников при вводе текста
  useEffect(() => {
    const searchEmployees = async () => {
      if (searchTerm.length < 2) {
        setEmployees([]);
        setIsOpen(false);
        return;
      }

      setIsLoading(true);
      try {
        const response = await EmployeeService.search(searchTerm);
        if (response.success) {
          setEmployees(response.data);
          setIsOpen(true);
        }
      } catch (error) {
        console.error('Ошибка поиска сотрудников:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const timeoutId = setTimeout(searchEmployees, 300); // Debounce
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Закрываем список при клике вне компонента
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        listRef.current &&
        !listRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);

    // Если пользователь очищает поле, сбрасываем выбор
    if (!newValue.trim()) {
      setSelectedEmployee(null);
      onChange(null);
    }
  };

  const handleEmployeeSelect = (employee: Employee) => {
    setSelectedEmployee(employee);
    setSearchTerm(`${employee.surname} ${employee.name} ${employee.patronymic}`.trim());
    setIsOpen(false);
    onChange(employee.id, employee);
  };

  const parseFullName = (fullName: string): { surname: string; name: string; patronymic: string } => {
    const parts = fullName.trim().split(/\s+/);
    return {
      surname: parts[0] || '',
      name: parts[1] || '',
      patronymic: parts[2] || ''
    };
  };

  const handleCreateNewEmployee = () => {
    const parsedName = parseFullName(searchTerm);
    setNewEmployeeData({
      surname: parsedName.surname,
      name: parsedName.name,
      patronymic: parsedName.patronymic,
      departmentId: departments.length > 0 ? departments[0].id : 0,
      jobIds: jobs.length > 0 ? [jobs[0].id] : [],
      mainJobId: jobs.length > 0 ? jobs[0].id : undefined
    });
    setShowCreateForm(true);
    setIsOpen(false);
  };

  const handleCreateEmployeeSubmit = async () => {
    if (!newEmployeeData.surname || !newEmployeeData.name || newEmployeeData.departmentId === 0) {
      return;
    }

    try {
      const response = await EmployeeService.create(newEmployeeData);
      if (response.success && response.data) {
        setSelectedEmployee(response.data);
        setSearchTerm(`${response.data.surname} ${response.data.name} ${response.data.patronymic}`.trim());
        setShowCreateForm(false);
        onChange(response.data.id, response.data);
      }
    } catch (error) {
      console.error('Ошибка создания сотрудника:', error);
    }
  };

  const handleCreateEmployeeCancel = () => {
    setShowCreateForm(false);
  };

  const handleInputFocus = () => {
    if (searchTerm.length >= 2 && employees.length > 0) {
      setIsOpen(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className="autocomplete-container" style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        className="form-input"
        placeholder={placeholder}
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        required={required}
        disabled={disabled}
        autoComplete="off"
      />

      {isOpen && (
        <ul
          ref={listRef}
          className="autocomplete-list"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderTop: 'none',
            borderRadius: '0 0 4px 4px',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 1000,
            margin: 0,
            padding: 0,
            listStyle: 'none'
          }}
        >
          {isLoading && (
            <li style={{ padding: '8px 12px', color: '#666' }}>
              Поиск...
            </li>
          )}

          {!isLoading && employees.length === 0 && searchTerm.length >= 2 && (
            <>
              <li style={{ padding: '8px 12px', color: '#666' }}>
                Сотрудники не найдены
              </li>
              <li
                onClick={handleCreateNewEmployee}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #eee',
                  backgroundColor: '#e8f5e8',
                  color: '#2d5a2d'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#d4edda';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#e8f5e8';
                }}
              >
                ➕ Создать нового сотрудника "{searchTerm}"
              </li>
            </>
          )}

          {!isLoading && employees.map((employee) => (
            <li
              key={employee.id}
              onClick={() => handleEmployeeSelect(employee)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                borderBottom: '1px solid #eee'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
              }}
            >
              {`${employee.surname} ${employee.name} ${employee.patronymic}`.trim()}
            </li>
          ))}
        </ul>
      )}

      {showCreateForm && (
        <div className="modal-overlay" style={{ zIndex: 2000 }}>
          <div
            className="modal-content"
            style={{ maxWidth: '600px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Создание нового сотрудника</h3>

            <div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Фамилия *
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={newEmployeeData.surname}
                  onChange={(e) => setNewEmployeeData(prev => ({ ...prev, surname: e.target.value }))}
                  required
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Имя *
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={newEmployeeData.name}
                  onChange={(e) => setNewEmployeeData(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Отчество
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={newEmployeeData.patronymic}
                  onChange={(e) => setNewEmployeeData(prev => ({ ...prev, patronymic: e.target.value }))}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Отдел *
                </label>
                <select
                  className="form-input"
                  value={newEmployeeData.departmentId}
                  onChange={(e) => setNewEmployeeData(prev => ({ ...prev, departmentId: Number(e.target.value) }))}
                  required
                >
                  <option value="">Выберите структурное подразделение</option>
                  {departments.map((department) => (
                    <option key={department.id} value={department.id}>
                      {department.name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Должность *
                </label>
                <select
                  className="form-input"
                  value={newEmployeeData.jobIds[0] || ''}
                  onChange={(e) => {
                    const jobId = Number(e.target.value);
                    setNewEmployeeData(prev => ({
                      ...prev,
                      jobIds: jobId ? [jobId] : [],
                      mainJobId: jobId || undefined
                    }));
                  }}
                  required
                >
                  <option value="">Выберите должность</option>
                  {jobs.map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCreateEmployeeCancel}
                >
                  Отмена
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleCreateEmployeeSubmit}
                  disabled={!newEmployeeData.surname || !newEmployeeData.name || newEmployeeData.departmentId === 0 || newEmployeeData.jobIds.length === 0}
                >
                  Создать сотрудника
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
